/**
 * Task operations — CRUD for tasks (create, complete, cancel, delete, restore, update fields).
 *
 * All operations return OpResult. Follows §23 principles:
 * - Named operations (not generic CRUD)
 * - Partial writes (only changed fields)
 * - Idempotent where possible
 * - No throws (except unexpected DB errors)
 */

import {
  ObjectType,
  ObjectStatus,
  StartAtType,
  StartAtSubType,
} from "./db-types.js"
import { createTaskDefaults, objectTypeLabel, toMsTimestamp } from "./utils.js"
import { getDb, callUndo, getNow, emitEvent } from "./runtime.js"
import { ok, fail } from "./op-types.js"
import type { OpResult, AddTaskOpts } from "./op-types.js"
import { getInsertIndex } from "./helpers/indexHelpers.js"
import { resolveContainerRealmId } from "./helpers/realmHelpers.js"
import { hasDocContext, contentToDocPair } from "./helpers/docHelpers.js"
import type { DocFormat } from "./helpers/docHelpers.js"
import { checklistParentPath, queryChecklistItems } from "./checklistOps.js"
import { cloneTaskContent, type ContentCloneProblem } from "./recurrenceOps.js"
import { partitionDuplicateReminders } from "./reminderOps.js"
import {
  loadDescendants,
  PURGE_CHUNK,
  purgeObjects,
  restoreCascade,
  trashCascade,
  uniqueDeletedAt,
} from "./helpers/trashHelpers.js"
import { serializeTaskMoves } from "./helpers/queueHelpers.js"
import { isClosableType, reopenContainer, reopenContainerIfClosed } from "./containerStatusOps.js"
import { runCompletionHooks } from "./taskCompletionHooks.js"

// ---------------------------------------------------------------------------
// Internal: bump recurrence version when template content changes
// ---------------------------------------------------------------------------

async function maybeBumpRecurrenceVersion(taskId: string): Promise<void> {
  const db = getDb()
  const task = await db.totodo.get(taskId)
  if (!task?.isRecurringTemplate) return
  const rec = await db.recurrence.get(taskId)
  if (!rec) return
  await db.recurrence.update(taskId, {
    version: rec.version + 1,
    updatedAt: getNow(),
    updatedBy: db.cloud.currentUserId,
  })
}

// ---------------------------------------------------------------------------
// Projects and lists are closed with closeContainer, not with the task status ops
// ---------------------------------------------------------------------------

/**
 * The refusal for completing / canceling a project or list as if it were a task: it may have open
 * tasks, which then stay open inside a closed container and vanish from Today and Upcoming.
 * closeContainer (containerStatusOps) asks what happens to them.
 */
function containerStatusRefusal(obj: { id?: string; type: ObjectType }, verb: "complete" | "cancel") {
  return fail(
    `${obj.id} is a ${obj.type === ObjectType.Project ? "project" : "collection"}: use closeContainer to ${verb} it ` +
      `(it decides what happens to the open tasks inside; ${verb}Task is for tasks)`,
  )
}

// ---------------------------------------------------------------------------
// addTask
// ---------------------------------------------------------------------------

export async function addTask(
  titleOrOpts: string | AddTaskOpts,
  parentPathLegacy?: string,
): Promise<OpResult<{ id: string }>> {
  const opts: AddTaskOpts =
    typeof titleOrOpts === "string"
      ? { title: titleOrOpts, parentPath: parentPathLegacy || undefined }
      : titleOrOpts

  const db = getDb()
  const id = opts.id ?? crypto.randomUUID()
  const timestamp = getNow()
  const parentPath = opts.parentPath || "root/#inbox"

  try {
    const index = await getInsertIndex(
      parentPath,
      opts.insertPosition ?? "bottom",
      opts.type ?? ObjectType.Task,
    )

    const realmId = opts.realmId ?? (await resolveContainerRealmId(parentPath))

    const task = createTaskDefaults({
      id,
      type: opts.type ?? ObjectType.Task,
      title: opts.title,
      note: opts.note ?? "",
      parentPath,
      index,
      status: ObjectStatus.Unfinished,
      tags: opts.tags ?? [],
      dueAt: toMsTimestamp(opts.dueAt),
      isDeleted: 0,
      realmId,
      owner: opts.owner ?? "",
      createdAt: timestamp,
      createdBy: "client",
      updatedAt: timestamp,
      updatedBy: "client",
      fullTextIndex: [],
      ...opts.fields,
    })
    // The row's identity is never taken from `fields` (the type refuses it; a cast must not get it through)
    task.id = id
    task.owner = opts.owner ?? ""
    task.realmId = realmId

    // Convert docContent → Y.Doc if provided and docContext is configured
    if (opts.docContent && hasDocContext()) {
      const { doc, docHtml } = await contentToDocPair(
        opts.docContent,
        opts.docFormat as DocFormat | undefined,
      )
      Object.assign(task, { doc, docHtml })
    }

    const addsOpenTask = (opts.type ?? ObjectType.Task) === ObjectType.Task
    const normalizedStartAt = toMsTimestamp(
      opts.startAt ?? (opts.startAtType != null ? String(getNow()) : undefined),
    )
    if (normalizedStartAt) {
      // When scheduling a new task with startAt, move it out of inbox → anyday
      const effectivePath = parentPath === "root/#inbox" ? "root/#anyday" : parentPath
      if (effectivePath !== parentPath) {
        task.parentPath = effectivePath
        task.index = await getInsertIndex(
          effectivePath,
          opts.insertPosition ?? "bottom",
          opts.type ?? ObjectType.Task,
        )
      }

      // An open task never goes into a closed project / list: that reopens the container, as late as
      // possible (a validation failure above writes nothing) and before the task (a failure from here
      // leaves an open container, never an open task in a closed one)
      if (addsOpenTask) await reopenContainerIfClosed(effectivePath)
      await db.transaction("rw", [db.totodo, db.startAtIndex], async () => {
        await db.totodo.add(task)

        const startAtIndex = await getInsertIndex(
          effectivePath,
          opts.insertPosition ?? "bottom",
          opts.type ?? ObjectType.Task,
          "startAtIndex",
        )
        await db.startAtIndex.add({
          id: `#${id}`,
          index: startAtIndex,
          startAt: normalizedStartAt,
          type: opts.startAtType ?? StartAtType.Default,
          subType: opts.startAtSubType ?? StartAtSubType.Default,
        })
      })
    } else {
      if (addsOpenTask) await reopenContainerIfClosed(parentPath)
      await db.totodo.add(task)
    }

    emitEvent("task_created", { id, type: objectTypeLabel(opts.type ?? ObjectType.Task), parentPath })
    return ok({ id })
  } catch (e) {
    return fail(`addTask failed: ${e instanceof Error ? e.message : String(e)}`)
  }
}

// ---------------------------------------------------------------------------
// setTaskDoc — set rich text content on a task
// ---------------------------------------------------------------------------

/**
 * Set or replace the rich text document on a task.
 *
 * Creates a Y.Doc from the provided content, then writes the binary update
 * directly to y-dexie's updates table (replacing any existing updates).
 * Also updates the docHtml field for search/preview.
 *
 * @param id - Task ID
 * @param content - Content string (text, HTML, Markdown, or ProseMirror JSON)
 * @param format - Optional format hint. Auto-detected if omitted.
 */
export async function setTaskDoc(
  id: string,
  content: string,
  format?: DocFormat,
): Promise<OpResult> {
  const db = getDb()
  try {
    const task = await db.totodo.get(id)
    if (!task) return fail(`Task not found: ${id}`)

    const { doc: newDoc, docHtml } = await contentToDocPair(content, format)

    // Write Y.Doc state to y-dexie's updates table
    // (same approach y-dexie uses internally on add())
    const Y = await import("yjs")
    const updatesTable = db.table("$totodo.doc_updates")
    const update = Y.encodeStateAsUpdateV2(newDoc)

    await db.transaction("rw", [db.totodo, updatesTable], async () => {
      // Remove existing Y.Doc updates for this task
      await updatesTable.where("k").equals(id).delete()
      // Write new Y.Doc state
      await updatesTable.add({ k: id, u: update, f: 1 })
      // Update docHtml as a normal field
      await db.totodo.update(id, {
        docHtml,
        updatedAt: getNow(),
      })
    })

    emitEvent("task_edited", { id, field: "description" })
    return ok()
  } catch (e) {
    return fail(
      `setTaskDoc failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Status changes
// ---------------------------------------------------------------------------

export async function completeTask(id: string, options?: { skipUndo?: boolean }): Promise<OpResult> {
  const db = getDb()
  try {
    const obj = await db.totodo.get(id)
    if (!obj) return fail(`Task not found: ${id}`)
    if (isClosableType(obj.type)) return containerStatusRefusal(obj, "complete")
    if (obj.status === ObjectStatus.Completed) return ok()

    const prevStatus = obj.status
    const prevStatusAt = obj.statusAt
    const timestamp = getNow()
    const isRecurring = !!(obj.isRecurrenceInstance && obj.recurringTemplateId)
    await db.totodo.update(id, {
      status: ObjectStatus.Completed,
      statusAt: timestamp,
      updatedAt: timestamp,
      // Set instanceOutcome atomically with status to avoid sync race
      ...(isRecurring ? { instanceOutcome: "completed" as const } : {}),
    })

    if (!options?.skipUndo) {
      callUndo(
        "Completed task",
        async () => {
          const db = getDb()
          await db.totodo.update(id, {
            status: prevStatus,
            statusAt: prevStatusAt,
            updatedAt: getNow(),
          })
        },
        async () => {
          const db = getDb()
          await db.totodo.update(id, {
            status: ObjectStatus.Completed,
            statusAt: getNow(),
            updatedAt: getNow(),
          })
        },
        "update",
      )
    }

    emitEvent("task_completed", { id })

    // Hooks: stop a running timer, record a recurrence instance's outcome / generate the next one,
    // advance a routine chain (shared with the bulk ops and closeContainer)
    await runCompletionHooks(obj, "completed")

    return ok()
  } catch (e) {
    return fail(
      `completeTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function uncompleteTask(id: string): Promise<OpResult> {
  const db = getDb()
  try {
    const obj = await db.totodo.get(id)
    if (!obj) return fail(`Task not found: ${id}`)
    // A project / list is reopened as the container it is (the container alone, its tasks keep their status)
    if (isClosableType(obj.type)) {
      const reopened = await reopenContainer(id)
      return reopened.ok ? ok() : reopened
    }
    if (obj.status === ObjectStatus.Unfinished) return ok()

    // An open task in a closed project / list reopens it (first: see reopenContainerIfClosed)
    if (obj.type === ObjectType.Task) await reopenContainerIfClosed(obj.parentPath)

    const prevStatus = obj.status
    const prevStatusAt = obj.statusAt
    const timestamp = getNow()
    await db.totodo.update(id, {
      status: ObjectStatus.Unfinished,
      statusAt: timestamp,
      updatedAt: timestamp,
    })

    callUndo(
      "Uncompleted task",
      async () => {
        const db = getDb()
        await db.totodo.update(id, {
          status: prevStatus,
          statusAt: prevStatusAt,
          updatedAt: getNow(),
        })
      },
      async () => {
        const db = getDb()
        await db.totodo.update(id, {
          status: ObjectStatus.Unfinished,
          statusAt: getNow(),
          updatedAt: getNow(),
        })
      },
      "update",
    )

    emitEvent("task_uncompleted", { id })
    return ok()
  } catch (e) {
    return fail(
      `uncompleteTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function cancelTask(id: string, options?: { skipUndo?: boolean }): Promise<OpResult> {
  const db = getDb()
  try {
    const obj = await db.totodo.get(id)
    if (!obj) return fail(`Task not found: ${id}`)
    if (isClosableType(obj.type)) return containerStatusRefusal(obj, "cancel")
    if (obj.status === ObjectStatus.Canceled) return ok()

    const prevStatus = obj.status
    const prevStatusAt = obj.statusAt
    const timestamp = getNow()
    await db.totodo.update(id, {
      status: ObjectStatus.Canceled,
      statusAt: timestamp,
      updatedAt: timestamp,
    })

    if (!options?.skipUndo) {
      callUndo(
        "Canceled task",
        async () => {
          const db = getDb()
          await db.totodo.update(id, {
            status: prevStatus,
            statusAt: prevStatusAt,
            updatedAt: getNow(),
          })
        },
        async () => {
          const db = getDb()
          await db.totodo.update(id, {
            status: ObjectStatus.Canceled,
            statusAt: getNow(),
            updatedAt: getNow(),
          })
        },
        "update",
      )
    }

    emitEvent("task_canceled", { id })

    // Hook: auto-stop running timer on cancellation
    await runCompletionHooks(obj, "canceled")

    return ok()
  } catch (e) {
    return fail(
      `cancelTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function setTaskStatus(
  id: string,
  status: ObjectStatus,
): Promise<OpResult> {
  const db = getDb()
  try {
    const obj = await db.totodo.get(id)
    if (!obj) return fail(`Task not found: ${id}`)
    if (isClosableType(obj.type)) {
      if (status === ObjectStatus.Unfinished) {
        const reopened = await reopenContainer(id)
        return reopened.ok ? ok() : reopened
      }
      if (obj.status !== status) {
        if (status === ObjectStatus.Completed) return containerStatusRefusal(obj, "complete")
        if (status === ObjectStatus.Canceled) return containerStatusRefusal(obj, "cancel")
      }
    }
    if (obj.status === status) return ok()

    if (status === ObjectStatus.Unfinished && obj.type === ObjectType.Task) {
      await reopenContainerIfClosed(obj.parentPath)
    }

    const timestamp = getNow()
    await db.totodo.update(id, {
      status,
      statusAt: timestamp,
      updatedAt: timestamp,
    })
    return ok()
  } catch (e) {
    return fail(
      `setTaskStatus failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Field updates
// ---------------------------------------------------------------------------

export async function updateTaskTitle(
  id: string,
  title: string,
): Promise<OpResult> {
  const db = getDb()
  try {
    await db.totodo.update(id, { title, updatedAt: getNow() })
    await maybeBumpRecurrenceVersion(id)
    emitEvent("task_edited", { id, field: "title" })
    return ok()
  } catch (e) {
    return fail(
      `updateTaskTitle failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function updateTaskNote(
  id: string,
  note: string,
): Promise<OpResult> {
  const db = getDb()
  try {
    await db.totodo.update(id, { note, updatedAt: getNow() })
    await maybeBumpRecurrenceVersion(id)
    emitEvent("task_edited", { id, field: "note" })
    return ok()
  } catch (e) {
    return fail(
      `updateTaskNote failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function updateTaskDueDate(
  id: string,
  dueAt: string | undefined,
): Promise<OpResult> {
  const db = getDb()
  try {
    await db.totodo.update(id, { dueAt: toMsTimestamp(dueAt) ?? dueAt, updatedAt: getNow() })
    emitEvent("task_edited", { id, field: "dueDate" })
    return ok()
  } catch (e) {
    return fail(
      `updateTaskDueDate failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function updateTaskTags(
  id: string,
  tags: string[],
): Promise<OpResult> {
  const db = getDb()
  try {
    await db.totodo.update(id, { tags, updatedAt: getNow() })
    emitEvent("task_edited", { id, field: "tags" })
    return ok()
  } catch (e) {
    return fail(
      `updateTaskTags failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Duplicate
// ---------------------------------------------------------------------------

/**
 * The id of the copy of reminder `reminderId` (the `i`-th one copied) of task `taskId` on its
 * copy `newId`: the original's offset suffix (`rem_<taskId>_2` -> `rem_<newId>_2`), else by
 * position (`rem_<newId>`, `rem_<newId>_1`, ...), as recurring instances number theirs.
 */
function copiedReminderId(
  reminderId: string | undefined,
  taskId: string,
  newId: string,
  i: number,
): string {
  const prefix = `rem_${taskId}`
  if (reminderId?.startsWith(prefix)) return `rem_${newId}${reminderId.slice(prefix.length)}`
  return i === 0 ? `rem_${newId}` : `rem_${newId}_${i}`
}

/** What of a duplicated task's content could not be copied (see duplicateTask). */
export type DuplicateIncompletePart = "files" | "document" | "links"

/**
 * Copy a task: title, note, tags, due date, user status, document (Y.Doc and the files attached
 * to the task, with image links pointing at the copies), checklist, schedule and reminders. The
 * copy is open and sits right below the original.
 *
 * Files attached to checklist rows (pasted images) are NOT copied: the copied rows have fresh ids
 * and no attachments, as a recurring instance's cloned checklist has none either.
 *
 * Reminders: the copy gets the set the original actually fires (partitionDuplicateReminders), with
 * ids derived from the copy's id (`rem_<newId>`, `rem_<newId>_1`, ...) — the deterministic scheme
 * that reconcile keeps whole. With random ids it would keep only one of a multi-offset set and
 * soft-delete the rest on every device.
 *
 * @param options.reminders - Copy the reminders (default true). The server leaves them out for
 *   a grant without reminders:write.
 * @param options.newId - Id for the copy instead of a random one (see AddTaskOpts.id).
 *
 * The copy of the document's content (files, Y.Doc, links) is best-effort and must not fail the
 * duplicate: the copy exists by then. When part of it could not be copied the result is still
 * `ok`, with `incomplete` (which parts: "files" | "document" | "links") and `warnings` (one
 * sentence each, with the error) so every caller can say so instead of reporting a clean copy.
 * Both are absent when everything was copied.
 */
export async function duplicateTask(
  id: string,
  options?: { reminders?: boolean; newId?: string },
): Promise<OpResult<{ newId: string; incomplete?: DuplicateIncompletePart[]; warnings?: string[] }>> {
  const db = getDb()
  try {
    const orig = await db.totodo.get(id)
    if (!orig) return fail(`Task not found: ${id}`)

    const timestamp = getNow()
    const newId = options?.newId ?? crypto.randomUUID()

    // The copy is open: next to a task of a closed project / list it reopens the container
    await reopenContainerIfClosed(orig.parentPath)

    const newTask = createTaskDefaults({
      id: newId,
      type: ObjectType.Task,
      title: orig.title,
      note: orig.note,
      tags: [...orig.tags],
      parentPath: orig.parentPath,
      index: orig.index + 0.001,
      status: ObjectStatus.Unfinished,
      dueAt: orig.dueAt,
      userStatus: orig.userStatus,
      subType: orig.subType,
      createdAt: timestamp,
      updatedAt: timestamp,
      owner: orig.owner,
      realmId: orig.realmId,
      docHtml: orig.docHtml,
    })

    const [checklist, schedule, reminders] = await Promise.all([
      queryChecklistItems(checklistParentPath(orig.parentPath, id)),
      db.startAtIndex.get(`#${id}`),
      options?.reminders === false
        ? []
        : db.reminder.where("[taskId+isDeleted]").equals([id, 0]).toArray(),
    ])
    // Scheduled right after the original, in the same day / evening / someday slot
    const scheduleIndex = schedule
      ? await getInsertIndex(newTask.parentPath, { afterId: id }, ObjectType.Task, "startAtIndex")
      : undefined

    await db.transaction("rw", [db.totodo, db.startAtIndex, db.reminder], async () => {
      await db.totodo.add(newTask)

      if (schedule && scheduleIndex !== undefined) {
        await db.startAtIndex.add({ ...schedule, id: `#${newId}`, index: scheduleIndex })
      }

      // Reminders: same time and kind, attached to the copy (ids: see the doc comment)
      if (reminders.length > 0) {
        await db.reminder.bulkAdd(
          partitionDuplicateReminders(reminders).kept.map((r, i) => ({
            ...r,
            id: copiedReminderId(r.id, id, newId, i),
            taskId: newId,
            parentPath: newTask.parentPath,
            createdAt: timestamp,
            updatedAt: timestamp,
          })),
        )
      }

      if (checklist.length === 0) return

      // The checklist comes along: fresh ids, same order, state and indent,
      // in the copy's realm (resolved the way addChecklistItem does).
      // `checklist` is in display order (compareChecklistRows); the copies get 0, 1, 2, … in
      // that order. Keeping the source's indexes would not keep it: tied or unusable indexes are
      // ordered by id, and the copies' ids are new — the rows would come out shuffled, and an
      // indented row could land under another parent.
      const clPath = checklistParentPath(newTask.parentPath, newId)
      const realmId = await resolveContainerRealmId(clPath)
      await db.totodo.bulkAdd(
        checklist.map((row, i) =>
          createTaskDefaults({
            id: crypto.randomUUID(),
            type: ObjectType.Checklist,
            subType: row.subType,
            title: row.title,
            note: row.note ?? "",
            tags: [...(row.tags ?? [])],
            parentPath: clPath,
            index: i,
            status: row.status,
            statusAt: row.statusAt,
            progress: row.progress,
            userStatus: row.userStatus,
            depth: row.depth,
            owner: row.owner,
            realmId,
            createdAt: timestamp,
            updatedAt: timestamp,
          }),
        ),
      )
    })

    // The document: its Y.Doc and attached files, with image links pointing at the
    // copies (as for a recurring instance). Non-fatal: docHtml is already copied, and the copy
    // exists - what could not be copied is reported in the result instead.
    const problems: ContentCloneProblem[] = []
    const docHtml = await cloneTaskContent(db, id, newId, orig.docHtml, undefined, undefined, problems)
    let linkError: string | undefined
    if (docHtml !== orig.docHtml) {
      try {
        await db.totodo.update(newId, { docHtml })
      } catch (e) {
        linkError = e instanceof Error ? e.message : String(e)
      }
    }
    const incomplete: DuplicateIncompletePart[] = []
    const warnings: string[] = []
    for (const p of problems) {
      if (!incomplete.includes(p.part)) incomplete.push(p.part)
      warnings.push(
        p.part === "files"
          ? `Attached files were not all copied (${p.error}); the copy's document may still link to the original's files.`
          : `The document's rich-text content was not copied (${p.error}); the copy has only the document's HTML.`,
      )
    }
    if (linkError !== undefined) {
      incomplete.push("links")
      warnings.push(`The copy's document links still point at the original's files (${linkError}).`)
    }

    callUndo(
      "Duplicated task",
      async () => {
        await deleteTask(newId, { silent: true })
      },
      async () => {
        await restoreFromTrash(newId, { silent: true })
      },
      "create",
    )

    return ok(incomplete.length > 0 ? { newId, incomplete, warnings } : { newId })
  } catch (e) {
    return fail(
      `duplicateTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Delete / Trash
// ---------------------------------------------------------------------------

/**
 * Soft-delete a task (move to trash). Idempotent.
 * Cascades: all children get soft-deleted too.
 *
 * @param options.silent - Skip undo registration.
 */
export async function deleteTask(
  id: string,
  options?: { silent?: boolean },
): Promise<OpResult> {
  const db = getDb()
  try {
    const obj = await db.totodo.get(id)
    if (!obj) return fail(`Task not found: ${id}`)
    if (obj.isDeleted === 1) return ok()

    // Hook: auto-stop running timer before deletion
    if (obj.timerStartedAt) {
      const { stopTimer } = await import("./timeTrackingOps.js")
      await stopTimer(id)
    }

    // Under the task's queue (serializeTaskMoves), one at a time with its moves and with
    // deleteChecklistItem of its rows: the cascade writes deletedAt to the rows it read, and a
    // row deleted on its own between that read and the write would get the task's stamp and
    // come back with the task (dexie-sqlite does not isolate the two). So the task and its
    // descendants are read there, and every row stamped is one still live or still in the trash
    // with its task at write time.
    const outcome = await serializeTaskMoves([id], async () => {
      const current = await db.totodo.get(id)
      if (!current) return "missing" as const
      if (current.isDeleted === 1) return "already" as const
      const timestamp = getNow()

      await db.transaction("rw", [db.totodo], async () => {
        // deletedAt is what restoreFromTrash goes by: what this cascade deletes gets
        // the object's deletedAt, a checklist row the user had already deleted keeps
        // its own, and the two never match (see trashHelpers).
        // Read before writing: dexie-sqlite does not roll back a failed transaction.
        const descendants = await loadDescendants(current)
        const cascaded = trashCascade(descendants)
        const deletedAt = uniqueDeletedAt(timestamp, descendants, cascaded)

        await db.totodo.update(id, {
          isDeleted: 1,
          deletedAt,
          updatedAt: timestamp,
        })
        await db.totodo.bulkUpdate(
          cascaded.map((d) => ({
            key: d.id as string,
            changes: {
              isDeleted: 1 as const,
              deletedAt,
              updatedAt: timestamp,
            },
          })),
        )
      })
      return "trashed" as const
    })
    if (outcome === "missing") return fail(`Task not found: ${id}`)
    if (outcome === "already") return ok()

    if (!options?.silent) {
      callUndo(
        "Deleted task",
        async () => {
          await restoreFromTrash(id)
        },
        async () => {
          await deleteTask(id)
        },
        "delete",
      )
    }

    emitEvent("task_deleted", { id })
    return ok()
  } catch (e) {
    return fail(
      `deleteTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export const moveToTrash = deleteTask

/**
 * Restore a soft-deleted task from trash. Idempotent.
 * Also restores all cascade-deleted children. A checklist row comes back only
 * when deleteTask's cascade deleted it (its deletedAt equals the object's), not
 * when it was deleted on its own before.
 */
export async function restoreFromTrash(
  id: string,
  options?: { silent?: boolean },
): Promise<OpResult> {
  const db = getDb()
  try {
    const obj = await db.totodo.get(id)
    if (!obj) return fail(`Task not found: ${id}`)
    if (obj.isDeleted === 0) return ok()

    // Under the task's queue, as deleteTask: a row deleteChecklistItem takes out of the task's
    // cascade (it gives the row a stamp of its own) is never restored from a stale read.
    const outcome = await serializeTaskMoves([id], async () => {
      const current = await db.totodo.get(id)
      if (!current) return "missing" as const
      if (current.isDeleted === 0) return "already" as const
      // An open task restored into a closed project / list reopens the container (first)
      if (current.type === ObjectType.Task && current.status === ObjectStatus.Unfinished) {
        await reopenContainerIfClosed(current.parentPath)
      }
      const timestamp = getNow()

      await db.transaction("rw", [db.totodo], async () => {
        // Checklist rows only when this object's delete cascade took them (see trashHelpers).
        const restored = restoreCascade(current, await loadDescendants(current))

        await db.totodo.update(id, {
          isDeleted: 0,
          updatedAt: timestamp,
        })
        await db.totodo.bulkUpdate(
          restored.map((d) => ({
            key: d.id as string,
            changes: { isDeleted: 0 as const, updatedAt: timestamp },
          })),
        )
      })
      return "restored" as const
    })
    if (outcome === "missing") return fail(`Task not found: ${id}`)
    if (outcome === "already") return ok()

    if (!options?.silent) {
      callUndo(
        "Restored from trash",
        async () => {
          await deleteTask(id)
        },
        async () => {
          await restoreFromTrash(id)
        },
      )
    }

    return ok()
  } catch (e) {
    return fail(
      `restoreFromTrash failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export const restoreTask = restoreFromTrash

/**
 * Permanently hard-delete a single task. Cleans up what hangs on it: its checklist rows (a delete
 * leaves them live under the task, so they would be orphans), its schedule row, reminders and
 * document updates.
 */
export async function permanentlyDeleteTask(id: string): Promise<OpResult> {
  const db = getDb()
  try {
    const task = await db.totodo.get(id)
    await purgeObjects([{ id, parentPath: task?.parentPath ?? "" }])
    return ok()
  } catch (e) {
    return fail(
      `permanentlyDeleteTask failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Convert
// ---------------------------------------------------------------------------

export async function convertTaskToSection(id: string): Promise<OpResult> {
  const db = getDb()
  try {
    const task = await db.totodo.get(id)
    if (!task) return fail(`Task not found: ${id}`)

    const timestamp = getNow()
    const childPath = `${task.parentPath}/${task.id}`

    const children = await db.totodo
      .where("parentPath")
      .equals(childPath)
      .filter((c) => c.isDeleted === 0)
      .sortBy("index")

    const siblings = await db.totodo
      .where("parentPath")
      .equals(task.parentPath)
      .filter((o) => o.isDeleted === 0 && o.id !== id)
      .sortBy("index")

    const currentIndex = task.index
    let nextIdx = currentIndex + children.length + 1
    for (const s of siblings) {
      if (s.index > currentIndex) {
        nextIdx = s.index
        break
      }
    }

    const spacing = (nextIdx - currentIndex) / (children.length + 1)

    await db.totodo.update(id, {
      type: ObjectType.Section,
      updatedAt: timestamp,
    })

    for (let i = 0; i < children.length; i++) {
      const child = children[i]!
      await db.totodo.update(child.id as string, {
        type: ObjectType.Task,
        parentPath: task.parentPath,
        index: currentIndex + spacing * (i + 1),
        updatedAt: timestamp,
      })
    }

    try {
      await db.startAtIndex.delete(`#${id}`)
    } catch {
      /* may not exist */
    }

    return ok()
  } catch (e) {
    return fail(
      `convertTaskToSection failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

export async function convertTaskToProject(
  id: string,
): Promise<OpResult<{ projectId: string }>> {
  const db = getDb()
  try {
    const task = await db.totodo.get(id)
    if (!task) return fail(`Task not found: ${id}`)

    const timestamp = getNow()
    const childPath = `${task.parentPath}/${task.id}`

    const children = await db.totodo
      .where("parentPath")
      .equals(childPath)
      .filter((c) => c.isDeleted === 0)
      .sortBy("index")

    const rootContainers = await db.totodo
      .where("parentPath")
      .equals("root")
      .filter(
        (o) =>
          o.isDeleted === 0 &&
          (o.type === ObjectType.Project ||
            o.type === ObjectType.Area ||
            o.type === ObjectType.List),
      )
      .toArray()

    const indices = rootContainers.map((o) => o.index)
    const newIndex = indices.length > 0 ? Math.max(...indices) + 1 : 0

    await db.totodo.update(id, {
      type: ObjectType.Project,
      parentPath: "root",
      index: newIndex,
      updatedAt: timestamp,
    })

    for (let i = 0; i < children.length; i++) {
      const child = children[i]!
      await db.totodo.update(child.id as string, {
        type: ObjectType.Task,
        parentPath: `root/${id}`,
        index: i,
        updatedAt: timestamp,
      })
    }

    try {
      await db.startAtIndex.delete(`#${id}`)
    } catch {
      /* may not exist */
    }

    return ok({ projectId: id })
  } catch (e) {
    return fail(
      `convertTaskToProject failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}

// ---------------------------------------------------------------------------
// Empty Trash
// ---------------------------------------------------------------------------

export async function emptyTrash(): Promise<OpResult<{ count: number }>> {
  const db = getDb()
  try {
    const currentUserId = db.cloud.currentUserId

    // The keys of the whole trash first (a key-only index read: a large trash is never held as
    // rows), then one page of rows at a time. Every object takes along what hangs on it - above all
    // the checklist rows of a task, which a delete leaves live under it (see purgeObjects).
    const trashedIds = await db.totodo.where("isDeleted").equals(1).primaryKeys()
    let count = 0
    let purged = 0
    for (let i = 0; i < trashedIds.length; i += PURGE_CHUNK) {
      const rows = await db.totodo.bulkGet(trashedIds.slice(i, i + PURGE_CHUNK))
      // The keys were read a while ago (sync may have run since): what is no longer in the trash - a
      // task restored on another device meanwhile - stays. `!!` since SQLite may hand back `true`.
      const page = rows.filter((t): t is NonNullable<typeof t> => !!t && !!t.isDeleted && t.owner === currentUserId && !!t.id)
      if (page.length === 0) continue
      await purgeObjects(page)
      purged += page.length
      // A checklist row in the trash is removed with its task when that comes first (another page
      // of the snapshot), so counting rows would depend on where a page ends: only objects count.
      count += page.filter((t) => t.type !== ObjectType.Checklist).length
    }

    if (purged === 0) return ok({ count: 0 })

    emitEvent("trash_emptied", { count })
    return ok({ count })
  } catch (e) {
    return fail(
      `emptyTrash failed: ${e instanceof Error ? e.message : String(e)}`,
    )
  }
}
